import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  getAllQuestions, 
  createQuestion, 
  updateQuestion, 
  deactivateQuestion,
  cloneQuestion,
  bulkUpdateQuestions 
} from '../../services/questionService';
import { publishSnapshot } from '../../services/versionService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import AdminQuestionForm from './AdminQuestionForm';
import AdminQuestionsReorder from './AdminQuestionsReorder';
import './AdminPages.css';

const AdminQuestions = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filters, setFilters] = useState({
    active: 'all', // 'all', 'active', 'inactive'
    pillar: 'all', // 'all', 'LEAD', 'EVOLVE', 'ALIGN', 'PERFORM'
    type: 'all' // 'all', 'scored', 'bonus'
  });
  const [pillarTotals, setPillarTotals] = useState({
    LEAD: 0,
    EVOLVE: 0,
    ALIGN: 0,
    PERFORM: 0
  });
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState({
    type: '', // 'pillar', 'weight', 'activate', 'deactivate'
    value: ''
  });

  const PILLAR_TARGETS = {
    LEAD: 20,
    EVOLVE: 25,
    ALIGN: 20,
    PERFORM: 10
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    applyFilters();
    calculatePillarTotals();
  }, [questions, filters]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const data = await getAllQuestions();
      setQuestions(data);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...questions];

    if (filters.active !== 'all') {
      filtered = filtered.filter(q => 
        filters.active === 'active' ? q.active : !q.active
      );
    }

    if (filters.pillar !== 'all') {
      filtered = filtered.filter(q => q.pillar === filters.pillar);
    }

    if (filters.type !== 'all') {
      filtered = filtered.filter(q => q.type === filters.type);
    }

    setFilteredQuestions(filtered);
  };

  const calculatePillarTotals = () => {
    const totals = {
      LEAD: 0,
      EVOLVE: 0,
      ALIGN: 0,
      PERFORM: 0
    };

    questions.forEach(q => {
      if (q.type === 'scored' && q.active) {
        totals[q.pillar] += (q.maxScore || 5) * (q.weight || 1);
      }
    });

    setPillarTotals(totals);
  };

  const handleCreate = () => {
    setEditingQuestion(null);
    setShowForm(true);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this question? It will be hidden from new assessments but preserved in historical data.')) {
      try {
        await deactivateQuestion(id, user?.uid);
        fetchQuestions();
      } catch (error) {
        console.error('Error deactivating question:', error);
        alert('Error deactivating question: ' + error.message);
      }
    }
  };

  const handleClone = async (id) => {
    try {
      await cloneQuestion(id, user?.uid);
      fetchQuestions();
      alert('Question cloned successfully! The cloned question is inactive by default.');
    } catch (error) {
      console.error('Error cloning question:', error);
      alert('Error cloning question: ' + error.message);
    }
  };

  const handleFormSubmit = async (questionData) => {
    try {
      if (editingQuestion) {
        await updateQuestion(editingQuestion.id, questionData, user?.uid);
      } else {
        await createQuestion(questionData, user?.uid);
      }
      fetchQuestions();
      setShowForm(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error('Error saving question:', error);
      throw error;
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      await publishSnapshot(user?.uid, 'Published from admin panel');
      setShowPublishDialog(false);
      alert('Snapshot published successfully!');
    } catch (error) {
      console.error('Error publishing snapshot:', error);
      alert('Error publishing snapshot: ' + error.message);
    } finally {
      setPublishing(false);
    }
  };

  const getPillarDeviation = (pillar) => {
    const current = pillarTotals[pillar];
    const target = PILLAR_TARGETS[pillar];
    const deviation = current - target;
    return { current, target, deviation };
  };

  const hasDeviations = () => {
    return Object.keys(PILLAR_TARGETS).some(pillar => {
      const { deviation } = getPillarDeviation(pillar);
      return Math.abs(deviation) > 0;
    });
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map(q => q.id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedQuestions.length === 0) {
      alert('Please select at least one question');
      return;
    }

    if (!bulkAction.type) {
      alert('Please select a bulk action');
      return;
    }

    try {
      let updates = {};

      switch (bulkAction.type) {
        case 'pillar':
          if (!bulkAction.value) {
            alert('Please select a pillar');
            return;
          }
          updates = { pillar: bulkAction.value };
          break;
        
        case 'weight':
          const weight = parseFloat(bulkAction.value);
          if (isNaN(weight) || weight <= 0) {
            alert('Please enter a valid positive weight');
            return;
          }
          updates = { weight };
          break;
        
        case 'activate':
          updates = { active: true };
          break;
        
        case 'deactivate':
          updates = { active: false };
          break;
        
        default:
          alert('Invalid bulk action');
          return;
      }

      const results = await bulkUpdateQuestions(selectedQuestions, updates, user?.uid, user?.email);
      
      if (results.failed.length > 0) {
        alert(`Bulk update completed with ${results.success.length} successes and ${results.failed.length} failures.\n\nFailed: ${results.failed.map(f => f.reason).join(', ')}`);
      } else {
        alert(`Successfully updated ${results.success.length} questions`);
      }

      // Reset selections and refresh
      setSelectedQuestions([]);
      setShowBulkActions(false);
      setBulkAction({ type: '', value: '' });
      fetchQuestions();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Error performing bulk action: ' + error.message);
    }
  };

  if (loading) return <Loader />;

  if (showForm) {
    return (
      <AdminQuestionForm
        question={editingQuestion}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingQuestion(null);
        }}
      />
    );
  }

  if (showReorder) {
    return (
      <AdminQuestionsReorder
        questions={questions}
        onBack={() => setShowReorder(false)}
        onSave={() => {
          setShowReorder(false);
          fetchQuestions();
        }}
      />
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Questions</h1>
          <p>Manage assessment questions</p>
          {selectedQuestions.length > 0 && (
            <p style={{ color: '#4caf50', marginTop: '0.5rem' }}>
              {selectedQuestions.length} question{selectedQuestions.length > 1 ? 's' : ''} selected
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {selectedQuestions.length > 0 && (
            <Button onClick={() => setShowBulkActions(!showBulkActions)} variant="secondary">
              ⚡ Bulk Actions
            </Button>
          )}
          <Button onClick={() => setShowReorder(true)} variant="secondary">
            ⇅ Reorder
          </Button>
          <Button onClick={() => setShowPublishDialog(true)} variant="secondary">
            📸 Publish Snapshot
          </Button>
          <Button onClick={handleCreate}>
            + Add Question
          </Button>
        </div>
      </div>

      {/* Pillar Totals Summary */}
      <div className="admin-form-card" style={{ marginBottom: '2rem' }}>
        <h3>Pillar Maximum Scores</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
          {Object.keys(PILLAR_TARGETS).map(pillar => {
            const { current, target, deviation } = getPillarDeviation(pillar);
            const isDeviated = Math.abs(deviation) > 0;
            return (
              <div key={pillar} style={{ 
                padding: '1rem', 
                background: isDeviated ? 'rgba(255, 193, 7, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                borderRadius: '8px',
                border: `2px solid ${isDeviated ? '#ffc107' : '#4caf50'}`
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{pillar}</div>
                <div>Current: {current}</div>
                <div>Target: {target}</div>
                {isDeviated && (
                  <div style={{ color: '#ffc107', marginTop: '0.5rem' }}>
                    {deviation > 0 ? '+' : ''}{deviation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {hasDeviations() && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px' }}>
            ⚠️ Warning: Pillar totals deviate from targets. This may affect scoring consistency.
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="admin-form-card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedQuestions.length === filteredQuestions.length && filteredQuestions.length > 0}
                onChange={handleSelectAll}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>Select All</span>
            </label>
          </div>
          <div style={{ borderLeft: '1px solid #333', height: '30px' }}></div>
          <div>
            <label style={{ marginRight: '0.5rem' }}>Status:</label>
            <select 
              value={filters.active} 
              onChange={(e) => setFilters({ ...filters, active: e.target.value })}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #333' }}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label style={{ marginRight: '0.5rem' }}>Pillar:</label>
            <select 
              value={filters.pillar} 
              onChange={(e) => setFilters({ ...filters, pillar: e.target.value })}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #333' }}
            >
              <option value="all">All</option>
              <option value="LEAD">LEAD</option>
              <option value="EVOLVE">EVOLVE</option>
              <option value="ALIGN">ALIGN</option>
              <option value="PERFORM">PERFORM</option>
            </select>
          </div>
          <div>
            <label style={{ marginRight: '0.5rem' }}>Type:</label>
            <select 
              value={filters.type} 
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #333' }}
            >
              <option value="all">All</option>
              <option value="scored">Scored</option>
              <option value="bonus">Bonus</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions Panel */}
      {showBulkActions && selectedQuestions.length > 0 && (
        <div className="admin-form-card" style={{ marginBottom: '2rem', background: 'rgba(76, 175, 80, 0.05)' }}>
          <h3>Bulk Actions ({selectedQuestions.length} selected)</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Action:</label>
              <select 
                value={bulkAction.type} 
                onChange={(e) => setBulkAction({ type: e.target.value, value: '' })}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #333', minWidth: '150px' }}
              >
                <option value="">Select action...</option>
                <option value="pillar">Change Pillar</option>
                <option value="weight">Adjust Weight</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
              </select>
            </div>

            {bulkAction.type === 'pillar' && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>New Pillar:</label>
                <select 
                  value={bulkAction.value} 
                  onChange={(e) => setBulkAction({ ...bulkAction, value: e.target.value })}
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #333' }}
                >
                  <option value="">Select pillar...</option>
                  <option value="LEAD">LEAD</option>
                  <option value="EVOLVE">EVOLVE</option>
                  <option value="ALIGN">ALIGN</option>
                  <option value="PERFORM">PERFORM</option>
                </select>
              </div>
            )}

            {bulkAction.type === 'weight' && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>New Weight:</label>
                <input 
                  type="number" 
                  min="0.1" 
                  step="0.1"
                  value={bulkAction.value} 
                  onChange={(e) => setBulkAction({ ...bulkAction, value: e.target.value })}
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #333', width: '100px' }}
                  placeholder="e.g., 1.5"
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={handleBulkAction} disabled={!bulkAction.type}>
                Apply
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => {
                  setShowBulkActions(false);
                  setBulkAction({ type: '', value: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="admin-page-content">
        {filteredQuestions.length === 0 ? (
          <div className="empty-state-card">
            <p>📝 No questions found. {filters.active !== 'all' || filters.pillar !== 'all' || filters.type !== 'all' ? 'Try adjusting filters.' : 'Click "Add Question" to create one.'}</p>
          </div>
        ) : (
          <div className="admin-grid">
            {filteredQuestions.map((question, index) => (
              <motion.div
                key={question.id}
                className="admin-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                style={{ 
                  border: selectedQuestions.includes(question.id) ? '2px solid #4caf50' : undefined 
                }}
              >
                <div className="admin-card-content">
                  <div className="admin-card-header">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => handleSelectQuestion(question.id)}
                        style={{ 
                          width: '18px', 
                          height: '18px', 
                          cursor: 'pointer',
                          marginTop: '0.25rem'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div>
                        <h3>{question.key}</h3>
                        <span style={{ fontSize: '0.85rem', color: '#888' }}>
                          {question.pillar} • {question.type}
                        </span>
                      </div>
                    </div>
                    <span className={`status-badge ${question.active ? 'active' : 'inactive'}`}>
                      {question.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p style={{ marginTop: '1rem' }}>{question.text.en}</p>
                  {question.text.mr && (
                    <p style={{ marginTop: '0.5rem', fontStyle: 'italic', color: '#888' }}>
                      {question.text.mr}
                    </p>
                  )}
                  <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
                    <span>Weight: {question.weight}</span>
                    <span>Max Score: {question.maxScore}</span>
                    <span>Order: {question.orderIndex}</span>
                  </div>
                  <div className="admin-card-footer" style={{ marginTop: '1rem' }}>
                    <div className="admin-card-actions">
                      <button onClick={() => handleEdit(question)} className="btn-edit">Edit</button>
                      <button onClick={() => handleClone(question.id)} className="btn-secondary">Clone</button>
                      <button onClick={() => handleDelete(question.id)} className="btn-delete">Deactivate</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Publish Dialog */}
      {showPublishDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3>Publish Snapshot</h3>
            <p style={{ marginTop: '1rem' }}>
              This will create a new version snapshot with all active questions. 
              Users will see this version in their assessments.
            </p>
            {hasDeviations() && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'rgba(255, 193, 7, 0.1)', 
                borderRadius: '8px',
                border: '1px solid #ffc107'
              }}>
                ⚠️ Warning: Pillar totals deviate from targets. Are you sure you want to publish?
              </div>
            )}
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                variant="secondary" 
                onClick={() => setShowPublishDialog(false)}
                disabled={publishing}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePublish}
                disabled={publishing}
              >
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestions;
